import { BASE_API_URL } from "config/consts";
import { httpConfig } from "config/http";
import { kebabCase } from "lodash";
import { Repository } from "react3l-common";
import { catchError, map, Observable, throwError } from "rxjs";

import nameof from "ts-nameof.macro";

import { API_PROJECT_DOCUMENT_USER_GROUPING_PREFIX } from "config/api-consts";
import {
  AppUserGrouping,
  AppUserGroupingFilter,
} from "models/AppUserGrouping";
import { RequestHistory, RequestHistoryFilter } from "models/RequestHistory";
import { AppUser, AppUserFilter } from "models/AppUser";
import { Status, StatusFilter } from "models/Status";
import { Organization, OrganizationFilter } from "models/Organization";

export type KeyType = string | number;

export class ProjectDocumentUserGroupingRepository extends Repository {
  constructor() {
    super(httpConfig);
    this.baseURL = new URL(
      API_PROJECT_DOCUMENT_USER_GROUPING_PREFIX,
      BASE_API_URL
    ).href;
  }

  // BE của project-document-user-grouping đặt tên field mapping đơn vị quản lý khác
  // với AppUserGrouping (appUserGroupingOrganizationMappings) — cả 2 chiều:
  // gửi lên là projectDocumentUserGroupingOrganizationMappings (toPayload), và trả về
  // (list/get/create/update) cũng là projectDocumentUserGroupingOrganizationMappings
  // (fromResponse). Đổi tên field ở đây, để phần model/hook/drawer dùng chung với màn
  // "Nhóm người dùng" gốc không cần biết sự khác biệt này.
  private toPayload = (data: AppUserGrouping): any => {
    const { appUserGroupingOrganizationMappings, ...rest } = data as any;
    return {
      ...rest,
      projectDocumentUserGroupingOrganizationMappings:
        appUserGroupingOrganizationMappings,
    };
  };

  private fromResponse = (item: AppUserGrouping): AppUserGrouping => {
    if (!item) {
      return item;
    }
    const { projectDocumentUserGroupingOrganizationMappings, ...rest } =
      item as any;
    return {
      ...rest,
      appUserGroupingOrganizationMappings:
        projectDocumentUserGroupingOrganizationMappings,
    };
  };

  // BE trả lỗi validate (400) cho field mapping đơn vị quản lý dưới key
  // projectDocumentUserGroupingOrganizationMappings, trong khi FormItem "Đơn vị quản lý"
  // dùng chung ở Drawer chỉ đọc model.errors.organization — đổi tên key lỗi ở đây để
  // Drawer bắt được lỗi này mà không cần biết field name riêng của API này.
  private fromErrorResponse = (error: any) => {
    const errors = error?.response?.data?.errors;
    if (
      errors &&
      Object.prototype.hasOwnProperty.call(
        errors,
        "projectDocumentUserGroupingOrganizationMappings"
      )
    ) {
      const { projectDocumentUserGroupingOrganizationMappings, ...rest } =
        errors;
      error.response.data.errors = {
        ...rest,
        organization: projectDocumentUserGroupingOrganizationMappings,
      };
    }
    return throwError(error);
  };

  public list = (
    filter?: AppUserGroupingFilter
  ): Observable<AppUserGrouping[]> => {
    return this.http
      .post<AppUserGrouping[]>(kebabCase(nameof(this.list)), filter)
      .pipe(
        Repository.responseMapToList<AppUserGrouping>(AppUserGrouping),
        map((items) => items.map(this.fromResponse))
      );
  };

  public count = (filter?: AppUserGroupingFilter): Observable<number> => {
    return this.http
      .post<number>(kebabCase(nameof(this.count)), filter)
      .pipe(Repository.responseDataMapper<number>());
  };

  public get = (id: number | string): Observable<AppUserGrouping> => {
    return this.http
      .post<AppUserGrouping>(kebabCase(nameof(this.get)), { id })
      .pipe(
        Repository.responseMapToModel<AppUserGrouping>(AppUserGrouping),
        map(this.fromResponse)
      );
  };

  public create = (data: AppUserGrouping): Observable<AppUserGrouping> => {
    return this.http
      .post<AppUserGrouping>(
        kebabCase(nameof(this.create)),
        this.toPayload(data)
      )
      .pipe(
        Repository.responseMapToModel<AppUserGrouping>(AppUserGrouping),
        map(this.fromResponse),
        catchError(this.fromErrorResponse)
      );
  };

  public update = (data: AppUserGrouping): Observable<AppUserGrouping> => {
    return this.http
      .post<AppUserGrouping>(
        kebabCase(nameof(this.update)),
        this.toPayload(data)
      )
      .pipe(
        Repository.responseMapToModel<AppUserGrouping>(AppUserGrouping),
        map(this.fromResponse),
        catchError(this.fromErrorResponse)
      );
  };

  public delete = (data: AppUserGrouping): Observable<AppUserGrouping> => {
    return this.http
      .post<AppUserGrouping>(kebabCase(nameof(this.delete)), data)
      .pipe(Repository.responseMapToModel<AppUserGrouping>(AppUserGrouping));
  };

  public save = (data: AppUserGrouping): Observable<AppUserGrouping> => {
    return data.id ? this.update(data) : this.create(data);
  };

  public listRequestHistory = (
    requestHistoryFilter?: RequestHistoryFilter
  ): Observable<RequestHistory[]> => {
    return this.http
      .post<RequestHistory[]>(
        kebabCase(nameof(this.listRequestHistory)),
        requestHistoryFilter
      )
      .pipe(Repository.responseMapToList<RequestHistory>(RequestHistory));
  };

  public countRequestHistory = (
    requestHistoryFilter?: RequestHistoryFilter
  ): Observable<number> => {
    return this.http
      .post<number>(
        kebabCase(nameof(this.countRequestHistory)),
        requestHistoryFilter
      )
      .pipe(Repository.responseDataMapper<number>());
  };

  public getRequestHistory = (
    versionId: number | string
  ): Observable<RequestHistory> => {
    return this.http
      .post<RequestHistory>(kebabCase(nameof(this.getRequestHistory)), {
        versionId,
      })
      .pipe(Repository.responseMapToModel<RequestHistory>(RequestHistory));
  };

  public countAppUser = (filter?: AppUserFilter): Observable<number> => {
    return this.http
      .post<number>(kebabCase(nameof(this.countAppUser)), filter)
      .pipe(Repository.responseDataMapper<number>());
  };

  public listAppUser = (filter?: AppUserFilter): Observable<AppUser[]> => {
    return this.http
      .post<AppUser[]>(kebabCase(nameof(this.listAppUser)), filter)
      .pipe(Repository.responseMapToList<AppUser>(AppUser));
  };

  // Dùng riêng cho danh sách chọn nhân viên ở popup "Thêm nhân viên".
  public filterListAppUser = (filter?: AppUserFilter): Observable<AppUser[]> => {
    return this.http
      .post<AppUser[]>(kebabCase(nameof(this.filterListAppUser)), filter)
      .pipe(Repository.responseMapToList<AppUser>(AppUser));
  };

  public removeAppUser = (data: AppUser): Observable<AppUser> => {
    return this.http
      .post<AppUser>(kebabCase(nameof(this.removeAppUser)), data)
      .pipe(Repository.responseMapToModel<AppUser>(AppUser));
  };

  public removeAppUserGrouping = (data: AppUser): Observable<AppUser> => {
    return this.http
      .post<AppUser>(kebabCase(nameof(this.removeAppUserGrouping)), data)
      .pipe(Repository.responseMapToModel<AppUser>(AppUser));
  };

  public bulkRemoveAppUser = (idList: KeyType[]): Observable<void> => {
    return this.http
      .post(kebabCase(nameof(this.bulkRemoveAppUser)), idList)
      .pipe(Repository.responseDataMapper());
  };

  public bulkAddAppUser = (data: any): Observable<AppUserGrouping> => {
    return this.http
      .post<AppUserGrouping>(kebabCase(nameof(this.bulkAddAppUser)), data)
      .pipe(Repository.responseMapToModel<AppUserGrouping>(AppUserGrouping));
  };

  public singleListStatus = (): Observable<Status[]> => {
    return this.http
      .post<Status[]>(
        kebabCase(nameof(this.singleListStatus)),
        new StatusFilter()
      )
      .pipe(Repository.responseMapToList<Status>(Status));
  };

  public filterListStatus = (): Observable<Status[]> => {
    return this.http
      .post<Status[]>(
        kebabCase(nameof(this.filterListStatus)),
        new StatusFilter()
      )
      .pipe(Repository.responseMapToList<Status>(Status));
  };

  public filterListOrganization = (
    filter: OrganizationFilter
  ): Observable<Organization[]> => {
    return this.http
      .post<Organization[]>(
        kebabCase(nameof(this.filterListOrganization)),
        filter
      )
      .pipe(Repository.responseMapToList<Organization>(Organization));
  };

  public singleListOrganization = (
    organizationFilter: OrganizationFilter
  ): Observable<Organization[]> => {
    return this.http
      .post<Organization[]>(
        kebabCase(nameof(this.singleListOrganization)),
        organizationFilter
      )
      .pipe(Repository.responseMapToList<Organization>(Organization));
  };

  public import = (
    file: File,
    name: string = nameof(file)
  ): Observable<void> => {
    const formData: FormData = new FormData();
    formData.append(name, file as Blob);
    return this.http
      .post<void>(kebabCase(nameof(this.import)), formData)
      .pipe(Repository.responseDataMapper<any>());
  };
}

export const projectDocumentUserGroupingRepository =
  new ProjectDocumentUserGroupingRepository();
